import React, { useState, useEffect, useRef } from "react";
import cn from "classnames";
import { useTransition, animated } from "react-spring";

import inputArrowSrc from "../../assets/icons/input-arrow.svg";

import styles from "./styles.module.scss";

export type SelectOption = {
  value: string;
  label: string;
};

type SelectInputProps = {
  id: string;
  label?: string;
  fieldName: string;
  selectedValue?: string;
  disabled?: boolean;
  filled?: boolean;
  options: SelectOption[];
  error?: { message: string };
  inputClassName?: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
};

export const SelectInput = ({
  id,
  label,
  fieldName,
  selectedValue,
  options,
  disabled = false,
  filled = false,
  error,
  inputClassName,
  setValue,
}: SelectInputProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);
  const [selectedOption, setSelectedOption] = useState<
    SelectOption | undefined
  >(
    options.find((option) => {
      if (option.value === "") return null;
      return option.value === selectedValue;
    })
  );

  useEffect(() => {
    const found = options.find((option) => {
      if (option.value === "") return false;
      return option.value === selectedValue;
    });
    setSelectedOption(found);
    if (found) {
      setValue(found.value);
    }
  }, [selectedValue, options, setValue]);

  const handleFocus = () => {
    if (disabled) return;
    setIsFocused(true);
  };

  const handleBlur = () => {
    if (disabled) return;

    setIsFocused(selectedValue !== "");
    setIsOpen(false);
  };

  const handleOptionClick = (option: SelectOption) => {
    if (disabled) return;
    setSelectedOption(option);
    setIsOpen(false);
    setValue(option.value);
  };

  const handleSelectClick = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const customDropdownTransitions = useTransition(isOpen, {
    from: { opacity: 0, transform: "translateY(-10px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(-10px)" },
  });

  return (
    <div
      ref={containerRef}
      className={cn(styles.selectContainer, { [styles.disabled]: disabled, [styles.open]: isOpen })}
    >
      <div
        className={cn(
          styles.customSelectValue,
          inputClassName,
          { [styles.disabled]: disabled },
          { [styles.focused]: isFocused || isOpen || filled }
        )}
        onClick={handleSelectClick}
        onBlur={handleBlur}
        onFocus={handleFocus}
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {label && <span className={styles.floatingLabel}>{label}</span>}

        {selectedOption && (
          <span className={styles.inputValue}>{selectedOption.label}</span>
        )}
        {!disabled && (
          <img
            src={inputArrowSrc}
            alt=""
            width={30}
            height={30}
            className={cn(styles.selectArrow, {
              [styles.selectArrowOpen]: isOpen,
            })}
          />
        )}
      </div>
      {customDropdownTransitions(
        (style, item) =>
          item && (
            <animated.div style={style} className={styles.customDropdown}>
              {options.map((option) => (
                <div
                  key={option.value}
                  className={styles.customOption}
                  onClick={() => handleOptionClick(option)}
                >
                  {option.label}
                </div>
              ))}
            </animated.div>
          )
      )}
      <select
        id={id}
        value={selectedOption?.value}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        className={styles.hiddenSelect}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <div
        className={cn(styles.errorBox, { [styles.visible]: !!error?.message })}
      >
        {!!error?.message && (
          <span className={styles.errorText}>{error.message}</span>
        )}
      </div>
    </div>
  );
};
